import {EditorSelection} from '@codemirror/state';

import {flatTypes, isSelectionBlank, isSingleCursor, tableTypes, typesInRange, unformattedTypes} from '../state.js';
import {changeByTrimmedRange, trimmedRange} from './ranges.js';
import {quotePadding} from "./padding.js";
import {onlyAllowed} from "../collections.js";

export function formatUnderline({state, dispatch}) {
  applyFormatStyleIfMissing(state, dispatch, '<u>', '</u>', 'underline', ['StrongEmphasis', 'Emphasis', 'Strikethrough']);
}

export function formatStrikeThrough({state, dispatch}) {
  if (selectionBreaksParagraph(state)) {
    return;
  }
  applyFormatStyleIfMissing(state, dispatch, '~~', '~~', 'strike', ['StrongEmphasis', 'Emphasis']);
}

export function formatItalics({state, dispatch}) {
  if (selectionBreaksParagraph(state)) {
    return;
  }
  applyFormatStyleIfMissing(state, dispatch, '*', '*', 'italics', ['StrongEmphasis', 'Strikethrough']);
}

export function formatBold({state, dispatch}) {
  if (selectionBreaksParagraph(state)) {
    return;
  }
  applyFormatStyleIfMissing(state, dispatch, '**', '**', 'bold', ['Emphasis', 'Strikethrough']);
}

function applyFormatStyleIfMissing(state, dispatch, mark, markClose, placeholder, otherStyles) {
  if (canApplyFormat(state, otherStyles)) {
    applyTextFormatStyle(state, dispatch, placeholder, mark, markClose);
  }
}

export function canStrikeThrough({state}) {
  return canApplyFormat(state, ['StrongEmphasis', 'Emphasis']);
}

export function canUnderline({state}) {
  return canApplyFormat(state, ['StrongEmphasis', 'Emphasis', 'Strikethrough']);
}

export function canItalics({state}) {
  return canApplyFormat(state, ['StrongEmphasis', 'Strikethrough']);
}

export function canBold({state}) {
  return canApplyFormat(state, ['Emphasis', 'Strikethrough']);
}

function canApplyFormat(state, otherStyles) {
  if (trimmedRangeLeavesRange(state, state.selection.main)) {
    return;
  }
  const types = typesInRange(state, state.selection.main);
  if (types.crossing.has('Link')) {
    return;
  }
  if (types.contained.has('Link')) {
    return onlyAllowed(flatTypes(types), [...unformattedTypes, ...tableTypes, 'EmphasisMark', 'Blockquote', ...otherStyles]);
  }
  return onlyAllowed(flatTypes(types), [...unformattedTypes, ...tableTypes, 'EmphasisMark', 'Blockquote', 'Link', 'LinkMark', 'URL', ...otherStyles]);
}

function trimmedRangeLeavesRange(state, {from, to}) {
  const content = state.sliceDoc(from, to);
  if (content.trim().length === 0) {
    return false;
  }
  const trimmedRange = {
    from: from + content.length - content.trimStart().length,
    to: to - (content.length - content.trimEnd().length)
  };
  return leavesRange(state, trimmedRange);
}

function leavesRange(state, range) {
  const {crossing} = typesInRange(state, range);
  return crossing.size > 0;
}

export function selectionBreaksParagraph(state) {
  return rangeBreaksParagraph(state, trimmedRange(state, state.selection.main));
}

function rangeBreaksParagraph(state, range) {
  const {containing} = typesInRange(state, range);
  return containing.has('Paragraph');
}

function applyTextFormatStyle(state, dispatch, placeholder, mark, markClose) {
  if (isSingleCursor(state) || isSelectionBlank(state)) {
    insertSelectedFormatMarker(state, dispatch, placeholder, mark, markClose);
  } else {
    surroundTrimmedSelectionWith(state, dispatch, mark, markClose);
  }
}

function insertSelectedFormatMarker(state, dispatch, placeholder, mark, markClose) {
  dispatch(changeByTrimmedRange(state, ({from, to}) => {
    const padding = quotePadding(state, from);
    return {
      changes: [{from, to, insert: padding + mark + placeholder + markClose}],
      range: EditorSelection.range(from + padding.length + mark.length, from + padding.length + mark.length + placeholder.length)
    };
  }));
}

function surroundTrimmedSelectionWith(state, dispatch, mark, markClose) {
  dispatch(changeByTrimmedRange(state, ({from, to}) => ({
    changes: [{from, insert: mark}, {from: to, insert: markClose}],
    range: EditorSelection.range(from + mark.length, to + mark.length)
  })));
}
